import { useState, useEffect } from 'react'
import { useLocation } from 'wouter'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { ArrowLeft, Save, Wand2 } from 'lucide-react'
import { useAuth } from '@/contexts/auth-context'
import { buildApiUrl } from '@/config/api'
import { AIAssistant } from '@/components/ai-assistant'
import toast from 'react-hot-toast'

interface RfpResponse {
  id: string
  message: string
  proposal: string
  budget: number
  timeline: string
  status: string
  attachments: string[]
  createdAt: string
  updatedAt: string
  rfp: {
    id: string
    title: string
    description: string
    requirements: string[]
    budgetMin: number
    budgetMax: number
    budgetCurrency: string
    deadline: string
    category: string
  }
}

export function EditResponse() {
  const { user } = useAuth()
  const [, setLocation] = useLocation()
  const [response, setResponse] = useState<RfpResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showAI, setShowAI] = useState(false)
  
  const [formData, setFormData] = useState({
    message: '',
    proposal: '',
    budget: '',
    timeline: '',
    attachments: [] as string[],
  })

  // Get response ID from URL
  const responseId = window.location.pathname.split('/').pop()

  useEffect(() => {
    if (!user || !responseId) {
      setLocation('/login')
      return
    }

    fetchResponse()
  }, [user, responseId])

  const fetchResponse = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Authentication required')
        setLocation('/login')
        return
      }

      const response = await fetch(buildApiUrl(`/api/responses/${responseId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        const responseData = data.data
        setResponse(responseData)
        
        // Populate form with existing data
        setFormData({
          message: responseData.message,
          proposal: responseData.proposal,
          budget: responseData.budget.toString(),
          timeline: responseData.timeline,
          attachments: responseData.attachments || [],
        })
      } else {
        const errorData = await response.json()
        toast.error(errorData.error || 'Failed to load response')
        setLocation('/my-responses')
      }
    } catch (error) {
      console.error('Error fetching response:', error)
      toast.error('Failed to load response')
      setLocation('/my-responses')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleAIContentGenerated = (content: string) => {
    // Parse the AI-generated content and update form fields
    const lines = content.split('\n')
    let currentSection = ''
    let proposal = ''
    let message = ''
    
    for (const line of lines) {
      const trimmedLine = line.trim()
      if (!trimmedLine) continue
      
      if (trimmedLine.toLowerCase().includes('message:') || trimmedLine.toLowerCase().includes('cover message:')) {
        currentSection = 'message'
        const messageContent = trimmedLine.split(':').slice(1).join(':').trim()
        if (messageContent) message = messageContent
      } else if (trimmedLine.toLowerCase().includes('proposal:') || trimmedLine.toLowerCase().includes('detailed proposal:')) {
        currentSection = 'proposal'
        const proposalContent = trimmedLine.split(':').slice(1).join(':').trim()
        if (proposalContent) proposal = proposalContent
      } else if (currentSection === 'message' && !trimmedLine.toLowerCase().includes('proposal:')) {
        message += (message ? '\n' : '') + trimmedLine
      } else if (currentSection === 'proposal') {
        proposal += (proposal ? '\n' : '') + trimmedLine
      }
    }
    
    // If no sections were found, treat the entire content as proposal
    if (!message && !proposal) {
      proposal = content.trim()
    }
    
    // Update form data
    if (message) {
      setFormData(prev => ({ ...prev, message: message.trim() }))
    }
    if (proposal) {
      setFormData(prev => ({ ...prev, proposal: proposal.trim() }))
    }
    
    setShowAI(false)
    toast.success('AI content applied to form!')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Validate form data
      if (!formData.message.trim() || formData.message.length < 10) {
        toast.error('Message must be at least 10 characters long')
        return
      }
      if (!formData.proposal.trim() || formData.proposal.length < 50) {
        toast.error('Proposal must be at least 50 characters long')
        return
      }
      if (!formData.budget || parseFloat(formData.budget) <= 0) {
        toast.error('Budget must be greater than 0')
        return
      }
      if (!formData.timeline.trim()) {
        toast.error('Timeline is required')
        return
      }

      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Please log in to update response')
        return
      }

      const updateData = {
        message: formData.message.trim(),
        proposal: formData.proposal.trim(),
        budget: parseFloat(formData.budget),
        timeline: formData.timeline.trim(),
        attachments: formData.attachments,
      }

      const updateResponse = await fetch(buildApiUrl(`/api/responses/${responseId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updateData),
      })

      if (updateResponse.ok) {
        toast.success('Response updated successfully!')
        setLocation(`/rfp/${response?.rfp.id}`)
      } else {
        const errorData = await updateResponse.json()
        toast.error(errorData.error || 'Failed to update response')
      }
    } catch (error) {
      console.error('Error updating response:', error)
      toast.error('Failed to update response')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading response...</p>
        </div>
      </div>
    )
  }

  if (!response) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Response not found</p>
          <Button 
            onClick={() => setLocation('/my-responses')} 
            className="mt-4"
          >
            Back to My Responses
          </Button>
        </div>
      </div>
    )
  }

  // Check if response can be edited (only pending responses)
  if (response.status !== 'PENDING') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-2">This response cannot be edited</p>
          <p className="text-sm text-gray-500">Only pending responses can be modified</p>
          <Button 
            onClick={() => setLocation(`/rfp/${response.rfp.id}`)} 
            className="mt-4"
          >
            View RFP
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => setLocation(`/rfp/${response.rfp.id}`)}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to RFP
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Edit Response</h1>
          <p className="mt-2 text-gray-600">
            Update your response to: <span className="font-medium">{response.rfp.title}</span>
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <Card className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Cover Message *
                  </label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    placeholder="Write a brief introduction and why you're interested in this project..."
                    className="min-h-[120px]"
                    required
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Minimum 10 characters ({formData.message.length}/10)
                  </p>
                </div>

                {/* Proposal */}
                <div>
                  <label htmlFor="proposal" className="block text-sm font-medium text-gray-700 mb-2">
                    Detailed Proposal *
                  </label>
                  <Textarea
                    id="proposal"
                    value={formData.proposal}
                    onChange={(e) => handleInputChange('proposal', e.target.value)}
                    placeholder="Provide a detailed proposal including your approach, methodology, deliverables..."
                    className="min-h-[200px]"
                    required
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Minimum 50 characters ({formData.proposal.length}/50)
                  </p>
                </div>

                {/* Budget and Timeline */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-2">
                      Budget ({response.rfp.budgetCurrency}) *
                    </label>
                    <Input
                      id="budget"
                      type="number"
                      min="1"
                      step="0.01"
                      value={formData.budget}
                      onChange={(e) => handleInputChange('budget', e.target.value)}
                      placeholder="Enter your budget"
                      required
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      RFP Budget: {response.rfp.budgetMin?.toLocaleString()} - {response.rfp.budgetMax?.toLocaleString()} {response.rfp.budgetCurrency}
                    </p>
                  </div>

                  <div>
                    <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 mb-2">
                      Timeline *
                    </label>
                    <Input
                      id="timeline"
                      value={formData.timeline}
                      onChange={(e) => handleInputChange('timeline', e.target.value)}
                      placeholder="e.g., 4-6 weeks"
                      required
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Deadline: {new Date(response.rfp.deadline).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end space-x-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setLocation(`/rfp/${response.rfp.id}`)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Updating...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Update Response
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* AI Assistant */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium">AI Assistant</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAI(!showAI)}
                >
                  <Wand2 className="mr-2 h-4 w-4" />
                  {showAI ? 'Hide' : 'Show'} AI
                </Button>
              </div>
              {showAI && (
                <AIAssistant
                  type="response"
                  initialData={{
                    rfpTitle: response.rfp.title,
                    rfpDescription: response.rfp.description,
                    rfpRequirements: response.rfp.requirements.join('\n'),
                    companyName: user?.company || 'Your Company',
                    companyDescription: '',
                    proposedSolution: formData.proposal,
                    pricing: formData.budget ? `${response.rfp.budgetCurrency} ${formData.budget}` : undefined,
                    timeline: formData.timeline,
                  }}
                  onContentGenerated={handleAIContentGenerated}
                />
              )}
            </Card>

            {/* RFP Summary */}
            <Card className="p-6">
              <h3 className="text-lg font-medium mb-4">RFP Summary</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Category:</span>
                  <span className="ml-2 text-gray-600">{response.rfp.category}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Budget:</span>
                  <span className="ml-2 text-gray-600">
                    {response.rfp.budgetMin?.toLocaleString()} - {response.rfp.budgetMax?.toLocaleString()} {response.rfp.budgetCurrency}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Deadline:</span>
                  <span className="ml-2 text-gray-600">
                    {new Date(response.rfp.deadline).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </Card>

            {/* Requirements */}
            <Card className="p-6">
              <h3 className="text-lg font-medium mb-4">Requirements</h3>
              <ul className="space-y-2">
                {response.rfp.requirements.map((req, index) => (
                  <li key={index} className="flex items-start">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span className="text-sm text-gray-600">{req}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
